import styled from '@emotion/styled';
import {
  Button,
  Card,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Link,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createElement, FC, MouseEvent, useContext, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';

import { Issue, IssueComment, User } from '../../../../generated/graphql';
import { toJSDate } from '../../../../graphql/datetime';
import useInputComment, { inputCommentLimit } from '../../../../hooks/useInputComment';
import { context as StateContext } from '../../../../view/context/state';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import Dialog from '../../primitive/DialogWithPadding';
import LfToBr from '../../primitive/LfToBr';
import { default as _AvatarAndName } from '../AvatarAndName';

interface Props {
  projectId: string;
  orgId: string;
  issue: Pick<Issue, 'id'> & {
    comments: Array<
      Pick<IssueComment, 'id' | 'body' | 'updatedAt'> & {
        user: Pick<User, 'name' | 'profileImageUrl' | 'id'>;
      }
    >;
  };
  className?: string;
  passedInspection: boolean;
  onUpdateProjectIssueComment: (commentId: string, comment: string) => void;
  onDeleteProjectIssueComment: (commentId: string) => void;
}

const ProjectIssueComments: FC<Props> = ({
  projectId,
  orgId,
  issue,
  className,
  passedInspection,
  onUpdateProjectIssueComment,
  onDeleteProjectIssueComment,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selected, setSelected] = useState('');
  const [editing, setEditing] = useState(false);
  const [inputComment, commentHandler] = useInputComment('');

  const state = useContext(StateContext);
  return (
    <Root className={className} id={issue.id}>
      {issue.comments.map((comment) => {
        return (
          <IssueItem key={`issue-item-${comment.id}`} id={comment.id}>
            {editing && selected == comment.id ? (
              <div>
                <CommentHeader>
                  <AvatarAndName user={comment.user} size={32} />
                </CommentHeader>
                <CommentForm error={inputComment.error}>
                  <Comment
                    value={inputComment.body}
                    variant="outlined"
                    rows="4"
                    rowsMax="8"
                    multiline
                    error={inputComment.error}
                    onChange={(e) => commentHandler.setComment(e.target.value)}
                  />
                  <InputCount>{`${inputComment.body.length}/${inputCommentLimit}`}</InputCount>
                </CommentForm>
                <Buttons>
                  <ActionButton
                    onClick={() => {
                      setEditing(false);
                      setSelected('');
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                    style={{ marginRight: 5 }}
                  >
                    <FormattedMessage id="show_project_page.project_issue.update_comment.cancel" />
                  </ActionButton>
                  <ActionButton
                    onClick={() => {
                      onUpdateProjectIssueComment(selected, inputComment.body);
                      setEditing(false);
                      setSelected('');
                    }}
                    color="primary"
                    variant="contained"
                    size="small"
                  >
                    <FormattedMessage id="show_project_page.project_issue.update_comment.update" />
                  </ActionButton>
                </Buttons>
              </div>
            ) : (
              <div>
                <CommentHeader>
                  <AvatarAndName user={comment.user} size={32} />
                  <Metadata>
                    <Link
                      component={RouterLink}
                      to={`/projects/${projectId}/brief?org=${orgId}&comment=${comment.id}`}
                      color="textSecondary"
                    >
                      <Updated variant="body2" color="textSecondary">
                        <FormattedMessage id="show_project_page.project_issue.comment_card.updated" />
                        {': '}
                        <FormattedDate
                          value={toJSDate(comment.updatedAt)}
                          year="numeric"
                          month="2-digit"
                          day="2-digit"
                          hour="2-digit"
                          minute="2-digit"
                        />
                      </Updated>
                    </Link>
                    {state.authentication.signedInUser?.id == comment.user.id ? (
                      <div style={{ textAlign: 'right', position: 'relative' }}>
                        {passedInspection ? (
                          <div>
                            <Tooltip
                              placement="top"
                              title={<FormattedMessage id="show_project_page.project_issue.comment_card.disabled" />}
                            >
                              <EditButton variant="body2" color="textSecondary">
                                <FormattedMessage id="show_project_page.project_issue.comment_card.edit_button" />
                              </EditButton>
                            </Tooltip>
                            <Tooltip
                              placement="top"
                              title={<FormattedMessage id="show_project_page.project_issue.comment_card.disabled" />}
                            >
                              <DeleteButton variant="body2" color="textSecondary">
                                <FormattedMessage id="show_project_page.project_issue.comment_card.delete_button" />
                              </DeleteButton>
                            </Tooltip>
                          </div>
                        ) : (
                          <div>
                            <EditButton
                              href="#"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                e.preventDefault();
                                setSelected(comment.id);
                                setEditing(true);
                                commentHandler.setComment(comment.body);
                              }}
                              variant="body2"
                              color="textSecondary"
                            >
                              <FormattedMessage id="show_project_page.project_issue.comment_card.edit_button" />
                            </EditButton>
                            <DeleteButton
                              href="#"
                              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                                e.preventDefault();
                                setSelected(comment.id);
                                setOpenDialog(true);
                              }}
                              variant="body2"
                              color="textSecondary"
                            >
                              <FormattedMessage id="show_project_page.project_issue.comment_card.delete_button" />
                            </DeleteButton>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </Metadata>
                </CommentHeader>

                <CommentCard>
                  <InputtedComment variant="subtitle1" component="p">
                    <LfToBr text={comment.body} />
                  </InputtedComment>
                </CommentCard>
              </div>
            )}
          </IssueItem>
        );
      })}
      <Dialog
        open={openDialog}
        onRequestClose={() => setOpenDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{''}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <FormattedMessage id="show_project_page.project_issue.delete_comment.dialog" />
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center' }}>
          <ActionButton
            onClick={() => {
              setOpenDialog(false);
              setSelected('');
            }}
            color="primary"
            variant="outlined"
          >
            <FormattedMessage id="show_project_page.project_issue.delete_comment.cancel" />
          </ActionButton>
          <ActionButton
            onClick={() => {
              onDeleteProjectIssueComment(selected);
              setOpenDialog(false);
              setSelected('');
            }}
            color="primary"
            variant="contained"
          >
            <FormattedMessage id="show_project_page.project_issue.delete_comment.delete" />
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

const Root = styled('div')();

const IssueItem = styled('div')({
  marginBottom: SPACING_REGULAR,
});

const CommentHeader = styled('div')(
  gridLayoutContainer({
    columns: ['auto', 'auto'],
    rows: ['auto'],
    areas: [['user', 'metadata']],
  }),
  {
    marginBottom: 1.5,
  },
);

const AvatarAndName = styled(_AvatarAndName)({
  ...gridLayoutItem('user', {
    justifySelf: 'start',
  }),
  marginBottom: SPACING_UNIT,
});

const Metadata = styled('div')({
  ...gridLayoutItem('metadata', {
    justifySelf: 'end',
    alignSelf: 'center',
  }),
  fontSize: 16,
});

const Updated = styled(Typography)({
  wordBreak: 'break-word',
  hyphens: 'auto',
});

const EditButton = styled(Link)({
  display: 'inline',
  marginRight: 5,
});
const DeleteButton = styled(Link)({
  display: 'inline',
});

const CommentCard = styled(Card)({
  width: '100%',
  padding: 16,
});

const InputtedComment = (styled(Typography)({
  wordBreak: 'break-word',
  hyphens: 'auto',
}) as unknown) as typeof Typography;

const CommentForm = styled(FormControl)({
  width: '100%',
  marginBottom: SPACING_UNIT,
});

const Comment = styled(TextField)({
  width: '100%',
});
const InputCount = styled(FormHelperText)({
  textAlign: 'right',
});
const Buttons = styled('div')({
  textAlign: 'right',
});
const ActionButton = styled(Button)({
  width: 100,
});

export default ProjectIssueComments;
